import { useTranslation } from 'react-i18next';
import { useSearchPage } from '../hooks/useSearchPage';
import { SearchBarAutocomplete } from '../../../../app/components/SearchBar/SearchBarAutocomplete';
import { useSearchSuggestions } from '../hooks/useSearchSuggestions';
import { esLang } from '../../application/types';
import './SearchPage.scss';

export default function SearchPage() {
  const { i18n } = useTranslation();
  const lang = esLang(i18n.language);
  const {
    t,
    q,
    inputValue,
    setInputValue,
    type,
    page,
    results,
    total,
    totalPages,
    isLoading,
    isError,
    onSubmit,
    onTypeChange,
    onPageChange,
    onResultClick,
    typeOptions,
  } = useSearchPage();
  const { fetchSuggestions } = useSearchSuggestions();

  return (
    <section className="search-page">
      <h1 className="search-page__title">{t('search.title')}</h1>

      <div className="search-page__bar">
        <SearchBarAutocomplete
          placeholder="search.placeholder"
          defaultValue={inputValue}
          value={inputValue}
          onChange={setInputValue}
          onSearch={onSubmit}
          onSuggestionsFetch={fetchSuggestions}
          onSuggestionSelect={(suggestion) => {
            if (suggestion.link) {
              onResultClick({
                id: suggestion.id,
                code: '',
                title: suggestion.title,
                subTitle: suggestion.subTitle,
                category: suggestion.category,
                description: suggestion.description,
                slug: '',
                status: '',
                link: suggestion.link,
              });
              return;
            }
            onSubmit(suggestion.title[lang]);
          }}
          minCharsForSuggestions={2}
          maxSuggestions={5}
          debounceDelay={300}
          isClearable
        />
      </div>

      <div className="search-page__filters" role="group" aria-label={t('search.filters')}>
        {typeOptions.map((option) => (
          <button
            key={option.value ?? 'all'}
            type="button"
            className={
              type === option.value
                ? 'search-page__filter search-page__filter--active'
                : 'search-page__filter'
            }
            onClick={() => onTypeChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>

      {q ? (
        <p className="search-page__meta">
          {t('search.resultsFor', { q, count: total })}
        </p>
      ) : (
        <p className="search-page__meta">{t('search.hint')}</p>
      )}

      {isLoading ? <p className="search-page__status">{t('search.loading')}</p> : null}
      {isError ? <p className="search-page__status">{t('search.error')}</p> : null}
      {!isLoading && !isError && q && results.length === 0 ? (
        <p className="search-page__status">{t('search.empty')}</p>
      ) : null}

      <ul className="search-page__list">
        {results.map((hit) => (
          <li key={`${hit.category?.key ?? 'hit'}-${hit.id}`}>
            <button
              type="button"
              className="search-page__row"
              onClick={() => onResultClick(hit)}
            >
              <span className="search-page__row-title">{hit.title[lang]}</span>
              {hit.subTitle?.[lang] ? (
                <span className="search-page__row-sub">{hit.subTitle[lang]}</span>
              ) : null}
              <span className="search-page__row-type">
                {hit.category?.[lang] || hit.category?.en}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {totalPages > 1 ? (
        <div className="search-page__pagination">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            {t('search.prev')}
          </button>
          <span>
            {page} / {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            {t('search.next')}
          </button>
        </div>
      ) : null}
    </section>
  );
}
